import PageShell from "../../components/PageShell";
import { useArticleJsonLd, ArticleHeader, OperatorLabCrumb, ArticleCTA, ArticleReactions, RelatedArticles } from "./_shared";

const SLUG = "aim-theory-101";
const PUBLISHED = "2026-02-15";
const HEADLINE = "Aim Theory 101: Tracking, Flicks, Micro-Corrections — and What Mouse Shape Has to Do With Any of It";
const META_DESC = "A plain-English breakdown of the three skills aim is really made of — tracking, flicks, micro-corrections — and how mouse shape, weight, hump placement, hand size and grip tension change the odds in each.";

// --- Pro mouse usage tables (ProSettings, usage share current to publish date) ---

const CS2_TOP = [
  { mouse: "Logitech G Pro X Superlight 2", share: "18.17% (171)", shape: "Ambidextrous", weight: "59 g" },
  { mouse: "Razer DeathAdder V4 Pro", share: "11.37% (107)", shape: "Ergonomic", weight: "56 g" },
  { mouse: "Logitech G Pro X2 SUPERSTRIKE", share: "10.20% (96)", shape: "Ambidextrous", weight: "61 g" },
  { mouse: "Razer Viper V3 Pro", share: "8.08% (76)", shape: "Ambidextrous", weight: "54 g" },
  { mouse: "Razer Viper V4 Pro", share: "7.65% (72)", shape: "Ambidextrous", weight: "49 g" },
];

const VAL_TOP = [
  { mouse: "Razer Viper V4 Pro", share: "17.43% (122)", shape: "Ambidextrous", weight: "49 g" },
  { mouse: "Razer Viper V3 Pro", share: "13.00% (91)", shape: "Ambidextrous", weight: "54 g" },
  { mouse: "Logitech G Pro X Superlight 2", share: "8.86% (62)", shape: "Ambidextrous", weight: "59 g" },
  { mouse: "Logitech G Pro X Superlight", share: "6.71% (47)", shape: "Ambidextrous", weight: "62 g" },
  { mouse: "Logitech G Pro X2 SUPERSTRIKE", share: "6.71% (47)", shape: "Ambidextrous", weight: "61 g" },
];

const APEX_TOP = [
  { mouse: "Logitech G Pro X Superlight", share: "9.20% (8)", shape: "Ambidextrous", weight: "62 g" },
  { mouse: "Logitech G Pro X Superlight 2", share: "6.90% (6)", shape: "Ambidextrous", weight: "59 g" },
  { mouse: "Razer Viper V3 Pro", share: "6.90% (6)", shape: "Ambidextrous", weight: "54 g" },
  { mouse: "Razer DeathAdder V3 Pro", share: "5.75% (5)", shape: "Ergonomic", weight: "63 g" },
  { mouse: "Razer Viper V4 Pro", share: "5.75% (5)", shape: "Ambidextrous", weight: "49 g" },
];

// --- Hand-size → mouse-length guidance (SSOT: content below) ---
const HAND_SIZE = [
  {
    hand: "Small",
    measure: "Under 17.5 cm long / under 8.5 cm wide",
    mouse: "115–120 mm",
  },
  {
    hand: "Medium",
    measure: "17.5–19.0 cm long / 8.5–9.5 cm wide",
    mouse: "120–125 mm",
  },
  {
    hand: "Large",
    measure: "Over 19.0 cm long / over 9.5 cm wide",
    mouse: "125 mm and above",
  },
];

// --- Shape → aim-style mapping (expanded 2026-02-15) ---
const SHAPE_MAP = [
  {
    archetype: "Large, low, centre-hump symmetrical",
    size: "127.1 × 63.9 × 39.9 mm / ~127 × 64 × 40 mm",
    band: "Large",
    weight: "49–54 g",
    hump: "Low, centre-focused, slightly flat",
    grip: "Claw, fingertip, palm — most grips",
    favors: "Micro-corrections + fast flicks; the pro default",
    examples: "Razer Viper V3 Pro, Razer Viper V4 Pro",
  },
  {
    archetype: "Medium, wide, centre-hump symmetrical",
    size: "124 × 66 × 38.7 mm",
    band: "Medium",
    weight: "59 g",
    hump: "Gradual, centred, flared tail",
    grip: "Claw first, palm and fingertip both viable",
    favors: "Micro-corrections with a big platform",
    examples: "VAXEE XE V2",
  },
  {
    archetype: "Medium-large \"safe\" symmetrical",
    size: "125 × 63.5 × 40 mm",
    band: "Large (on the line)",
    weight: "60–62 g",
    hump: "High, central",
    grip: "Palm and claw, works with everything",
    favors: "All-rounder; suits almost any grip",
    examples: "Logitech G Pro X Superlight 2, Superlight, G Pro X2 SUPERSTRIKE",
  },
  {
    archetype: "Small, rear-hump symmetrical",
    size: "115.6–118.4 × 61–63 × 36.6–38.2 mm",
    band: "Small",
    weight: "35–51 g",
    hump: "Rear-set, moderate",
    grip: "Claw, fingertip",
    favors: "Micro-corrections and snappy flick-stop",
    examples: "Lamzu Atlantis Mini, Pulsar X2 Crazylight Mini, Pulsar X2N Crazylight Mini",
  },
  {
    archetype: "Medium, low-hump symmetrical",
    size: "124 × 64 × 37 mm",
    band: "Medium",
    weight: "56 g",
    hump: "Low, flat",
    grip: "Fingertip, claw",
    favors: "Fast micro-corrections on a wide, low platform",
    examples: "Pulsar eS FS-1",
  },
  {
    archetype: "Large ergonomic, rear hump",
    size: "128 × 68 × 44 mm / ~129.5 mm long",
    band: "Large",
    weight: "56–63 g",
    hump: "Tall, rear, full palm fill",
    grip: "Palm, palm-claw",
    favors: "Sustained tracking, angle-holding",
    examples: "Razer DeathAdder V4 Pro, DeathAdder V3 Pro",
  },
  {
    archetype: "Large ergonomic, middle hump",
    size: "125 × 67.7 × 43.9 mm",
    band: "Large",
    weight: "60 g",
    hump: "Middle, high, wide",
    grip: "Palm",
    favors: "Sustained tracking with a wider base",
    examples: "Logitech G Pro X Superlight 2 DEX",
  },
  {
    archetype: "Medium ergonomic, gentler hump",
    size: "123 × 61 × 42 mm",
    band: "Medium",
    weight: "77 g",
    hump: "Lower, less aggressive",
    grip: "Palm-claw",
    favors: "Tracking and comfort over long sessions",
    examples: "ZOWIE EC2-CW",
  },
  {
    archetype: "Medium narrow ergonomic",
    size: "120 × 57 × 42 mm",
    band: "Medium (on the line)",
    weight: "60 g",
    hump: "Rear hump, narrow",
    grip: "Palm-claw, narrower hands",
    favors: "Tracking for smaller or slimmer hands",
    examples: "VAXEE E1 Wireless",
  },
  {
    archetype: "Small low-hump ergonomic hybrid",
    size: "118 × 57 × 37 mm",
    band: "Small",
    weight: "56 g",
    hump: "Low, flat",
    grip: "Palm, claw, fingertip all work",
    favors: "Genuinely good at everything — tracking, flicking, micro-corrections",
    examples: "VAXEE NP-01S",
  },
];

function MouseTable({ title, caption, rows }) {
  return (
    <div className="op-table-wrap" data-testid={`aim-theory-table-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
      <h4 className="mb-3 font-mono text-[11px] tracking-[0.14em] uppercase text-[#F59E0B]">{title}</h4>
      <table className="op-table">
        <thead>
          <tr>
            <th>Mouse</th>
            <th>Share</th>
            <th>Shape</th>
            <th>Weight</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.mouse}>
              <td data-label="Mouse"><strong>{r.mouse}</strong></td>
              <td data-label="Share">{r.share}</td>
              <td data-label="Shape">{r.shape}</td>
              <td data-label="Weight">{r.weight}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {caption && <p className="op-table-note">{caption}</p>}
    </div>
  );
}

export default function AimTheory101() {
  useArticleJsonLd({ slug: SLUG, headline: HEADLINE, description: META_DESC, datePublished: PUBLISHED });

  return (
    <PageShell description={META_DESC} title={`${HEADLINE} — Loadout Operator Lab`} path={`/operator-lab/${SLUG}`}>
      <OperatorLabCrumb />
      <ArticleHeader
        eyebrow="05 / Operator Lab · Tech Deep-Dive"
        kicker="Aim Theory, Mouse Shape & What the Pros Actually Use"
        headline={HEADLINE}
        subhead="Every week someone walks in and asks for &ldquo;the best aim mouse.&rdquo; Fair question, slightly wrong question. Because aim isn't one skill, and the mouse that helps you beam a sliding Octane in Apex is not automatically the mouse that helps you one-tap a jiggle-peek in CS2."
        readingTime="12 min read"
        datePublished={PUBLISHED}
      />

      <article className="operator-article" data-testid="operator-article-aim-theory-101">
        <p>
          So before we get to shapes, sizes and what the pros are actually buying, let's split aim into the three things it's really made of. Two minutes, promise.
        </p>

        <h2>The three pillars of aim (beginner version)</h2>
        <p>
          <strong>Tracking</strong> — staying on a target while it moves. You're not finding the enemy, you're staying glued to them. Think Apex, The Finals, Marvel Rivals. The failure isn't &ldquo;missed shot,&rdquo; it's &ldquo;was on the body and slowly drifted off the head.&rdquo;
        </p>
        <p>
          <strong>Micro-corrections</strong> — tiny, fast adjustments after your crosshair is already close. Your pre-aim lands a corner, someone peeks, and you nudge your crosshair maybe 15 pixels onto a head. This is CS2 and Valorant in a nutshell.
        </p>
        <p>
          <strong>Flicks</strong> — one big, fast throw across the pad. The hard part isn't the speed. It's the braking — decelerating at exactly the right moment. Overflicking is a braking problem, not a speed problem.
        </p>
        <p>
          Quick rule of thumb: low time-to-kill (TTK) and tight angles → micro-corrections matter most. High TTK and fast-moving targets → tracking. Instant-delete close-range duels → flicks.
        </p>

        <h2>Why your mouse shape matters (in plain English)</h2>
        <p>
          Two words you'll see everywhere: <strong>symmetrical (ambidextrous)</strong> and <strong>ergonomic</strong>.
        </p>
        <p>
          A symmetrical shell is neutral — no thumb shelf, no palm ramp. That neutrality means your fingers can move freely, you can re-grip easily, and you can lift the mouse to reset between flicks. ZOWIE's own description is that symmetrical mice <em>&ldquo;prioritize flexibility, fast repositioning, and better freedom of movement.&rdquo;</em>
        </p>
        <p>
          An ergonomic shell makes the opposite trade: it cradles your palm and locks your grip in. Great for long tracking fights and for comfort, and it can cost you a little flick speed and re-grip freedom.
        </p>
        <p>
          Then there's the <strong>hump</strong> — the highest point on the shell. This is the detail most people ignore. ProSettings puts it well: a centered hump <em>&ldquo;feels quite neutral and supports many grip styles,&rdquo;</em> a back-focused hump <em>&ldquo;fills the palm a lot more, leading to a more locked-in feeling,&rdquo;</em> and a flatter top <em>&ldquo;gives a lot more freedom, but can feel 'empty' if you prefer a mouse that fills your hand.&rdquo;</em>
        </p>
        <p>
          Finally, weight and size. Smaller, lighter mice feel more nimble because less of your hand touches the shell. Bigger mice give more support and stability. And on weight — lighter is not automatically better; there's just an upper limit where a mouse gets genuinely uncomfortable to aim with.
        </p>

        <h2>What the pros are actually using</h2>
        <p>
          We pulled current usage data from ProSettings. One thing jumps out instantly: the light, ambidextrous shell has eaten the pro scene.
        </p>

        <MouseTable
          title="Most used mouse in CS2 (941 pros tracked)"
          rows={CS2_TOP}
          caption="Source: ProSettings pro-usage share"
        />

        <MouseTable
          title="Most used mouse in Valorant"
          rows={VAL_TOP}
          caption="Source: ProSettings pro-usage share"
        />

        <MouseTable
          title="Most used mouse in Apex Legends"
          rows={APEX_TOP}
          caption="Source: ProSettings pro-usage share"
        />

        <p>
          Across all 2,431 pros ProSettings tracks, the top five are the Superlight 2 (12.34%), Viper V3 Pro (10.98%), Viper V4 Pro (9.75%), Superlight (9.13%) and G Pro X2 SUPERSTRIKE (7.49%). Every one ambidextrous, every one light.
        </p>
        <p>
          Two honesty notes: Marvel Rivals has a pro list, but the per-mouse share data wouldn't load for us, and The Finals has essentially no pro-peripheral coverage. So we're not going to invent numbers for those two. If you main them, the trend still points the same way — the Rivals pros we can see are on the same light ambi shells.
        </p>

        <h2>Sizing a mouse: measure your hand first</h2>
        <p>
          Before you pick an archetype, measure your hand. Thirty seconds, and it narrows the shelf immediately.
        </p>
        <p>
          Rest your hand flat on a table. <strong>Length</strong> is from the tip of your middle finger to the first crease at your wrist. <strong>Width</strong> is across your knuckles with your fingers together.
        </p>

        <div className="op-table-wrap" data-testid="aim-theory-table-hand-size">
          <table className="op-table">
            <thead>
              <tr>
                <th>Your hand</th>
                <th>Measure</th>
                <th>Mouse length to aim for</th>
              </tr>
            </thead>
            <tbody>
              {HAND_SIZE.map((r) => (
                <tr key={r.hand}>
                  <td data-label="Your hand"><strong>{r.hand}</strong></td>
                  <td data-label="Measure">{r.measure}</td>
                  <td data-label="Mouse length to aim for">{r.mouse}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p>
          Those are the bands we use for the rest of this article, so here's the rule stated once so it stays consistent: <strong>120 mm counts as medium, and 125 mm counts as large.</strong> That puts the Superlight family, the DEX and both Vipers on the large side of the line, the VAXEE E1 and Pulsar eS FS-1 sitting right on the medium boundary, and the small rear-hump shapes like the Atlantis Mini tucked neatly inside the small band.
        </p>
        <p>
          A useful sanity check from the mouse community: your ideal mouse length tends to land just under about 60% of your hand length. And a reminder that this is a starting point, not a verdict — plenty of large-handed players happily fingertip a small shell because that's what their grip wants.
        </p>

        <h2>Shape and size mapped to aim style</h2>
        <p>
          <strong>Heads up:</strong> this is a map of where the odds point, not a rulebook. ProSettings is blunt that predicting the right mouse from hand size and grip type alone is <em>&ldquo;bogus.&rdquo;</em> Use it as a starting shortlist, then trust your own hand.
        </p>

        <div className="op-table-wrap" data-testid="aim-theory-table-shape-map">
          <table className="op-table">
            <thead>
              <tr>
                <th>Archetype</th>
                <th>Size (L × W × H)</th>
                <th>Band</th>
                <th>Weight</th>
                <th>Hump</th>
                <th>Natural grip</th>
                <th>Favors</th>
                <th>Example mice</th>
              </tr>
            </thead>
            <tbody>
              {SHAPE_MAP.map((r) => (
                <tr key={r.archetype}>
                  <td data-label="Archetype"><strong>{r.archetype}</strong></td>
                  <td data-label="Size (L × W × H)">{r.size}</td>
                  <td data-label="Band">{r.band}</td>
                  <td data-label="Weight">{r.weight}</td>
                  <td data-label="Hump">{r.hump}</td>
                  <td data-label="Natural grip">{r.grip}</td>
                  <td data-label="Favors">{r.favors}</td>
                  <td data-label="Example mice">{r.examples}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p>
          Now the details, because this is where it gets interesting.
        </p>
        <p>
          The <strong>Viper V3 and V4 Pro</strong> are large symmetrical shells with a low, flat, centre-focused hump. EloShapes logs the V3 Pro at 127.1 × 63.9 × 39.9 mm and 54 g, and files its hump as &ldquo;back – minimal,&rdquo; which really just means the hump is so flat and central it barely registers from the side. That flatness is why it works with basically any grip. It doesn't force your hand anywhere, so claw, fingertip and palm players all get along with it. The <strong>VAXEE XE V2</strong> sits in the same family but a band down, at 124 × 66 × 38.7 mm and 59 g — and that 66 mm width is why people describe it as a big mouse even though it's technically medium length. ProSettings calls its hump &ldquo;centrally placed&rdquo; and &ldquo;gradual,&rdquo; with a pronounced flare at the back for locking in, and their reviewer calls it perfect for claw grip, with palm and fingertip both viable.
        </p>
        <p>
          The <strong>Superlight, Superlight 2 and G Pro X2 SUPERSTRIKE</strong> are the same shape, full stop. EloShapes has the Superlight 2 and the SUPERSTRIKE at an identical 125.0 × 63.5 × 40.0 mm with a centre-placed hump — the only differences are switches and a gram of weight. This is the famous &ldquo;potato&rdquo;: large, safe, high central hump, and it's the shape that flatters palm and claw grip most while still working with everything else. That's exactly why it's the most-used shape in the world.
        </p>
        <p>
          The <strong>small rear-hump symmetricals</strong> are the micro-correction specialists, and they're all genuinely in the small band: Lamzu Atlantis Mini at 117 × 63 × 37 mm and 49–51 g, Pulsar X2 Crazylight Mini at 115.6 × 61 × 36.6 mm and 35–37 g, and Pulsar X2N Crazylight Mini at 118.4 × 62.4 × 38.2 mm and 38–40 g. Pulsar's own listing describes the X2 Crazylight's shape as a &ldquo;back-moderate hump&rdquo; aimed at fingertip and claw grip. A rear hump locks your palm heel in and keeps your fingers free to make tiny, fast movements — precisely the CS2 and Valorant job. If you want the same family in a medium shell, the Pulsar X2 Crazylight Medium measures 120.4 × 63 × 38 mm at 39–41 g, which lands it just inside the medium band.
        </p>
        <p>
          The <strong>Pulsar eS FS-1</strong> is the odd one out, and note it's medium, not large, despite looking substantial: 124 × 64 × 37 mm and 56 g. A deliberately low hump on a wide, long platform is the combo you want if you like a big-feeling surface for fingertip or claw grip without a hump poking into your palm. Pulsar lists it as optimised for palm and claw grip, and a hump this flat fingertip-grips naturally too.
        </p>
        <p>
          On the ergo side, the <strong>DeathAdder V3 and V4 Pro</strong> are large, full-feeling rear-hump ergos — the V4 Pro comes in at 128 × 68 × 44 mm and 56 g, and that deep palm fill is exactly what you want for a long tracking fight. The <strong>Superlight 2 DEX</strong> is the same idea with a middle hump and a wider 67.7 mm body at 125 × 67.7 × 43.9 mm and 60 g, so you get sustained-tracking support with a flatter feel across the top. The <strong>EC2-CW</strong> is the more comfortable, less aggressive sibling: 123 × 61 × 42 mm, a medium-length shell with a gentler hump that doesn't force your hand into one position — which is why it's been a comfort pick for over a decade, even at 77 g. The <strong>VAXEE E1</strong> is the compact narrow ergo at 120 × 57 × 42 mm and 60 g — it lands right on the medium boundary and its 57 mm width is the narrowest here, so it behaves like a small ergo in the hand. And the <strong>NP-01S</strong> is the quiet hero of this whole article at 118 × 57 × 37 mm and 56 g: a small, low, flat-hump ergonomic hybrid that is genuinely good at tracking, flicking and micro-corrections without being outstanding at any one of them.
        </p>

        <h2>One thing worth knowing before you shop</h2>
        <p>
          The mice we carry at Loadout are often clone shapes of the mainstream picks above — same shell proportions, same hump placement, same size class — and a lot of them ship with comparable, sometimes better, sensors and switches than the originals. Since shape is what actually decides whether you aim well, that's the whole point: you shouldn't have to pay flagship money to get the shell your hand wants.
        </p>
        <p>
          So when you're browsing our shelf and wondering which of ours matches that Viper or Superlight you keep reading about, compare mouse shapes on{" "}
          <a href="https://www.eloshapes.com/" target="_blank" rel="noopener noreferrer" data-testid="aim-theory-eloshapes-link">EloShapes</a>
          {" "}— it overlays real outlines so you can see instantly which budget clone mice line up with the mainstream model you're curious about. Find the shape, and the rest is just practice.
        </p>

        <h2>The twist: the data doesn't match the theory</h2>
        <p>
          If ergonomic shells were a tracking cheat code, Apex would be full of DeathAdders. It isn't — the most-used Apex mouse is the 62-gram ambidextrous Superlight, and the only ergo in the top five sits at 5.75%.
        </p>
        <p>
          Meanwhile CS2 — the micro-correction game — has the most ergonomic representation of any title here, with the DeathAdder V4 Pro at 11.37%.
        </p>
        <p>
          So ergonomic isn't winning because it's a tracking advantage. It's winning as a <em>fit</em> choice: big hands, true palm grip, years of muscle memory built around that thumb shelf. And the neutral shapes win everywhere because they're safe — ProSettings calls the Superlight <em>&ldquo;a very safe shape that can be used by just about anyone.&rdquo;</em>
        </p>
        <p>
          <strong>The corrected rule:</strong> symmetrical and light helps flicks, micro-corrections and tracking alike, because a neutral shape never fights you. Ergonomic helps you specifically if your hand and grip genuinely want palm support — and you pay for it with a bit of flick speed and re-grip freedom.
        </p>

        <h2>Where tension management fits</h2>
        <p>
          Tension has a bad reputation, but the honest version is this: some tension is necessary just to control the mouse, and more is necessary for fast flicks or tracking fast targets. It's a tool with a cost curve.
        </p>
        <p>
          <strong>Flicks</strong> want a tension spike. Brace, throw, release. The mistake is holding that tension after the shot — that's the post-flick shake.
        </p>
        <p>
          <strong>Tracking</strong> wants the opposite. A loose grip with just enough pressure to not drop the mouse. Sustained tension buys short-term stability at the cost of fatigue, and fatigue shows up as a slow loss of smoothness.
        </p>
        <p>
          <strong>Micro-corrections</strong> want it lightest of all. A tense wrist can't make a 3-pixel adjustment cleanly — it overshoots, corrects, overshoots. If your crosshair was basically on the head and then slid off, that wasn't sensitivity. That was tension.
        </p>
        <p>
          And here's the shape connection people miss: an ergonomic palm grip mechanically encourages you to brace into the shell, which makes it easy to build pressure you never release. A small symmetrical shell gives your fingers room to stay loose. That's the real reason symmetrical shapes help micro-corrections — not flick geometry, but a shape that doesn't tempt you into gripping.
        </p>

        <h2>Don't forget the pad and the screen</h2>
        <p>
          Your mouse is one part of a system. Mousepad friction changes how much tension you need to keep the mouse under control, which is why a fast pad and a light mouse are one purchase decision, not two. And the pro tier has standardized on 360 Hz monitors — tracking and micro-corrections are both feedback-loop skills, and you can't correct against information you can't see.
        </p>

        <h2>So what should you actually buy?</h2>
        <ol>
          <li><strong>Shape first, always.</strong> Nothing compensates for a mouse that doesn't fit your hand. Shape is king.</li>
          <li><strong>Measure your hand, then shortlist two or three archetypes.</strong> Small hands land in 115–120 mm, medium in 120–125 mm, large in 125 mm and above.</li>
          <li><strong>Then go light.</strong> The pro band is roughly 49–62 g. Low mass helps the flick brake and the micro-correction, and doesn't hurt tracking.</li>
          <li><strong>Then tune pad and sensitivity as one system.</strong> Low sensitivity for tac FPS micro-corrections, higher for tracking games like Apex.</li>
          <li><strong>Then train tension like a skill.</strong> Pulse it for flicks, drop it for tracking, keep it light for micro-corrections.</li>
        </ol>
        <p>
          Come in and try a few shapes on the floor — hands disagree with spreadsheets constantly, and that's exactly why we keep so many out. And if you want to shortcut the search, compare mouse shapes on{" "}
          <a href="https://www.eloshapes.com/" target="_blank" rel="noopener noreferrer">EloShapes</a>
          {" "}before you come in: find which of our clone shapes lines up with the mainstream mouse you've been eyeing, and you'll walk out with something you'll actually aim well with.
        </p>

        <ArticleReactions slug={SLUG} />
        <RelatedArticles currentSlug={SLUG} />
        <ArticleCTA label="Shop mice" plpName="Mice" />
      </article>
    </PageShell>
  );
}
